// KWF Admin sidebar — ported to the KWF Admin design artifact.
// Dark teal rail with Foundation / Platform / Pharmacy sections,
// user card + logout at the bottom.
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toggleAuth } from "../Redux/Slices/auth-slice";
import { fetchApi } from "../utlis/axios";
import { Icon } from "./admin/_adminApi";

// Group definitions. Keys match the previous role filter — "admin" are the
// KWF foundation operator pages, "pharmacist" are the dispensing shop pages,
// "staff" are Django is_staff IMS admin pages.
const GROUPS = [
  {
    label: "Foundation",
    role: "admin",
    items: [
      { href: "/dashboard_graph",    name: "Dashboard",       icon: Icon.apps },
      { href: "/applications",       name: "Applications",    icon: Icon.file },
      { href: "/program",            name: "Programs",        icon: Icon.package },
      { href: "/familyProgram",      name: "Sub-Programs",    icon: Icon.activity },
      { href: "/family_donation",    name: "Family Donation", icon: Icon.users },
      { href: "/kwf_donation",       name: "Donation",        icon: Icon.wallet },
      { href: "/kwf_medical_staff",  name: "Medical Stores",  icon: Icon.hospital },
    ],
  },
  {
    label: "Pharmacy",
    role: "pharmacist",
    items: [
      { href: "/verify_fingerprint", name: "Verify Fingerprint", icon: Icon.shield },
      { href: "/kwf_receipts",       name: "Receipts",           icon: Icon.file },
      { href: "/kwf_monthlyreport",  name: "Monthly Report",     icon: Icon.activity },
    ],
  },
  {
    label: "Platform",
    staffOnly: true,
    items: [
      { href: "/admin/app",      name: "App",   icon: Icon.apps },
      { href: "/admin/licences", name: "IMS",   icon: Icon.hospital },
      { href: "/admin/users",    name: "Users", icon: Icon.users },
    ],
  },
];

// For signed-in users with no section of their own (e.g. field staff), so the
// rail isn't empty. Top-level URLs, so these skip the /dashboard prefix.
const RECORDS_GROUP = {
  label: "Records",
  items: [
    { to: "/familylisting",      name: "Family Listing",   icon: Icon.users },
    { to: "/memberlisting",      name: "Member Listing",   icon: Icon.file },
    { to: "/propertydetailpage", name: "Property Listing", icon: Icon.hospital },
  ],
};

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { role, first_name, last_name, username, is_superuser: userIsSuper, is_staff: userIsStaff } =
    useSelector((s) => s.authSlice.userInfo?.user || {});
  const { access } = useSelector((s) => s.authSlice.userInfo || {});
  const [isStaff, setIsStaff] = useState(!!userIsStaff);
  const [isSuper, setIsSuper] = useState(!!userIsSuper);

  useEffect(() => {
    if (!access) return;
    fetchApi.get("admin/me/", { headers: { Authorization: `Bearer ${access}` } })
      .then((r) => {
        setIsStaff(!!r?.data?.is_staff);
        setIsSuper(!!r?.data?.is_superuser);
      })
      .catch(() => {});
  }, [access]);

  const logout = () => {
    dispatch(toggleAuth({ isLogin: false, userInfo: null }));
    navigate("/");
    localStorage.removeItem("activeTab");
  };

  // Superusers see every section, whatever foundation role they were given.
  // The backend creates these users as "pharmacist"; older ones are "medicalstaff".
  const rawRole = (role || "").toLowerCase();
  const roleName = rawRole === "medicalstaff" ? "pharmacist" : rawRole;
  const roleGroups = GROUPS.filter((g) => isSuper || (g.staffOnly ? isStaff : g.role === roleName));
  const visibleGroups = roleGroups.length ? roleGroups : [RECORDS_GROUP];

  const fullName = [first_name, last_name].filter(Boolean).join(" ") || username || "User";
  const initials = fullName.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "U";
  const subtitle = isSuper ? "Superuser" : isStaff ? "Staff" : (role || "User");

  const pathFor = (it) => it.to || `/dashboard${it.href}`;
  // Nested detail pages (e.g. /dashboard/admin/licences/:id) still mark the
  // parent "IMS" link active.
  const isActive = (it) => {
    const p = location.pathname;
    return p === pathFor(it) || p.startsWith(`${pathFor(it)}/`);
  };

  return (
    <aside className="ad-side">
      <div className="ad-brand">
        <div className="ad-brand-mark"><Icon.cross /></div>
        <div className="ad-brand-t">
          <strong>KWF</strong>
          <span>Khadim Welfare Foundation</span>
        </div>
      </div>
      <nav className="ad-nav">
        {visibleGroups.map((g) => (
          <React.Fragment key={g.label}>
            <div className="ad-sec">{g.label}</div>
            {g.items.map((it) => {
              const I = it.icon;
              return (
                <Link
                  key={pathFor(it)}
                  to={pathFor(it)}
                  className={`ad-link${isActive(it) ? " active" : ""}`}
                >
                  <I />
                  <span>{it.name}</span>
                </Link>
              );
            })}
          </React.Fragment>
        ))}
      </nav>
      <div className="ad-side-foot">
        <div className="ad-me">
          <div className="ad-me-av">{initials}</div>
          <div className="ad-me-t">
            <strong>{fullName}</strong>
            <span>{subtitle}</span>
          </div>
          <button className="ad-me-out" onClick={logout} title="Logout"><Icon.logOut /></button>
        </div>
      </div>
    </aside>
  );
}
